import { useCallback, useEffect, useState } from 'react'

import { faGreaterThan } from '@fortawesome/pro-regular-svg-icons'
import { Flex } from 'antd'
import { HStack, NumberInput, QuickTip } from 'govwell-ui'
import FormControl from 'govwell-ui/components/FormControl/FormControl'
import isNil from 'lodash.isnil'
import { DateTemplateInput, DelayType } from 'types/graphql'
import { v4 as uuid } from 'uuid'

import DateTemplateRelationSelect from 'src/components/DateTemplateInputs/DateTemplateRelationSelect'
import DateTemplateTypeSelect from 'src/components/DateTemplateInputs/DateTemplateTypeSelect'
import DaySelect from 'src/components/Selects/DaySelect'
import MonthSelect from 'src/components/Selects/MonthSelect'
import { TimeUnitSelect } from 'src/components/Selects/TimeUnitSelect'
import { useCurrentBreakpoint } from 'src/hooks/use-current-breakpoint'
import { Month } from 'src/types'
import { getNumberOfDaysForMonth } from 'src/utils/date'
import { NumberFormat } from 'src/utils/number'

import { DateTemplateRelation } from '../../models/Settings/Interval'

type Props = {
  caption?: React.ReactNode
  dateTemplate: Partial<DateTemplateInput>
  errorMessage?: React.ReactNode
  isClearable?: boolean
  isDateTemplateTypeHidden?: boolean
  isDisabled?: boolean
  dateTemplateRelationOptions?: DateTemplateRelation[]
  isInvalid?: boolean
  label?: React.ReactNode
  onChange: (value: Partial<DateTemplateInput>) => void
}
const DateTemplateInputs = ({
  caption,
  dateTemplate: propDateTemplate,
  errorMessage,
  isClearable,
  isDateTemplateTypeHidden = false,
  isDisabled,
  dateTemplateRelationOptions,
  isInvalid,
  label,
  onChange,
}: Props) => {
  // Track internal state to support partial input (missing fields) if the parent's onChange doesn't handle partial input
  const [dateTemplate, setDateTemplate] = useState(propDateTemplate)
  useEffect(() => {
    setDateTemplate(propDateTemplate)
  }, [propDateTemplate])

  const handleChange = useCallback(
    (newDateTemplate: Partial<DateTemplateInput>) => {
      setDateTemplate(newDateTemplate)
      onChange(newDateTemplate)
    },
    [onChange]
  )

  const [id] = useState(uuid())
  const { isSmallScreen } = useCurrentBreakpoint()
  const handleCountChange = useCallback(
    (newCount: number | undefined) => {
      handleChange({
        ...dateTemplate,
        interval: {
          ...dateTemplate.interval,
          count: newCount,
        },
      })
    },
    [dateTemplate, handleChange]
  )
  const handleTimeUnitChange = useCallback(
    (newTimeUnit: DelayType | undefined) => {
      handleChange({
        ...dateTemplate,
        interval: {
          ...dateTemplate.interval,
          type: newTimeUnit,
        },
      })
    },
    [dateTemplate, handleChange]
  )
  const handleDayChange = useCallback(
    (newDay: number | undefined) => {
      if (!dateTemplate.date || !newDay) {
        return
      }
      handleChange({
        ...dateTemplate,
        date: {
          ...dateTemplate.date,
          day: newDay,
        },
      })
    },
    [dateTemplate, handleChange]
  )
  const handleMinimumDelayDaysChange = useCallback(
    (newMinimumDelayDays: number | undefined) => {
      if (!dateTemplate.date) {
        return
      }
      handleChange({
        ...dateTemplate,
        date: { ...dateTemplate.date, minimumDelayDays: newMinimumDelayDays },
      })
    },
    [dateTemplate, handleChange]
  )
  const handleMonthChange = useCallback(
    (newMonth: number | undefined) => {
      if (isNil(newMonth)) {
        return
      }
      let newDay = dateTemplate.date?.day
      if (!newDay || newDay > getNumberOfDaysForMonth(newMonth)) {
        newDay = Month.January
      }
      handleChange({
        ...dateTemplate,
        date: {
          ...dateTemplate.date,
          month: newMonth,
          day: newDay,
        },
      })
    },
    [dateTemplate, handleChange]
  )
  return (
    <FormControl
      caption={caption}
      errorMessage={errorMessage}
      id={id}
      isInvalid={isInvalid}
      label={label}
    >
      {() => (
        <Flex gap="6px" wrap={isSmallScreen() ? 'wrap' : 'nowrap'} flex={1} align="flex-end">
          {!isDateTemplateTypeHidden && (
            <DateTemplateTypeSelect
              dateTemplate={dateTemplate}
              isClearable={isClearable}
              isDisabled={isDisabled}
              onChange={handleChange}
              width={isSmallScreen() ? '100%' : '134px'}
            />
          )}
          {dateTemplate.type === 'Interval' && (
            <Flex align="center" flex={3} gap="6px">
              <NumberInput
                ariaLabel="Count"
                isInvalid={isNil(dateTemplate.interval?.count) ? isInvalid : false}
                isDisabled={isDisabled}
                leftAddon={null}
                onValueChange={handleCountChange}
                placeholder="e.g. 3"
                value={dateTemplate.interval?.count}
                width="70px"
              />
              <TimeUnitSelect
                isInvalid={isNil(dateTemplate.interval?.type) ? isInvalid : false}
                isDisabled={isDisabled}
                onValueChange={handleTimeUnitChange}
                value={dateTemplate.interval?.type}
                width="110px"
              />
              <DateTemplateRelationSelect
                dateTemplate={dateTemplate}
                isDisabled={isDisabled}
                onChange={handleChange}
                options={dateTemplateRelationOptions}
              />
            </Flex>
          )}
          {dateTemplate.type === 'Date' && (
            <Flex align="center" flex={3} gap="6px">
              <MonthSelect
                isDisabled={isDisabled}
                onValueChange={handleMonthChange}
                value={dateTemplate.date?.month}
              />
              <DaySelect
                isDisabled={isDisabled}
                month={dateTemplate.date?.month}
                onValueChange={handleDayChange}
                value={dateTemplate.date?.day}
              />
              <NumberInput
                ariaLabel="Minimum delay days"
                min={0}
                format={NumberFormat.Integer}
                placeholder="min."
                prefixIcon={faGreaterThan}
                value={dateTemplate.date?.minimumDelayDays}
                onValueChange={handleMinimumDelayDaysChange}
                width="164px"
                suffix={
                  <HStack gap={1} className="basis-0">
                    <span>days</span>
                    <QuickTip content="Optionally enter the minimum number of days for this delay. For example, if the delay is configured as December 31 with a 60 day minimum, delays initiated on Nov 1 will schedule for the following calendar year (e.g. Nov 1 2024 -> Dec 31 2025)." />
                  </HStack>
                }
                isDisabled={isDisabled}
              />
            </Flex>
          )}
        </Flex>
      )}
    </FormControl>
  )
}

export default React.memo(DateTemplateInputs)
